import type {
  DataTypeConfig,
  InputValueDataTypeConfig,
  TsTypeOf,
} from '@helo/cms'

export const CMS_OPTIONS_TYPE_NAME = 'CmsOptions'

export type OptionsState = Record<string, unknown>

type OptionPresence<V> =
  | { required: true; defaultValue?: never }
  | { required?: false; defaultValue: V }
  | { required?: false; defaultValue?: undefined }

export type OptionCommon<K extends string = string> = {
  key: K
  label: string
  description: string
  category?: string
}

export type ControlCreateOptionInput =
  | (OptionCommon<string> & {
      inputKind: 'boolean'
    } & OptionPresence<boolean>)
  | (OptionCommon<string> & {
      inputKind: 'number'
      min?: number
      max?: number
      step?: number
    } & OptionPresence<number>)
  | (OptionCommon<string> & {
      inputKind: 'text' | 'color'
    } & OptionPresence<string>)
  | (OptionCommon<string> & {
      inputKind: 'select'
      selectChoices: readonly [
        { value: string; label: string },
        ...{ value: string; label: string }[],
      ]
    } & OptionPresence<string>)

export type CustomCreateOptionInput = OptionCommon<string> & {
  inputKind: string
  ofType: DataTypeConfig
} & OptionPresence<unknown>

export type CreateOptionInput =
  | ControlCreateOptionInput
  | CustomCreateOptionInput

export type CreateNestedOptionInput = OptionCommon<string> &
  OptionPresence<unknown> & {
    list?: true
  }

export type OptionFieldArray = readonly InputValueDataTypeConfig[]

export type OptionRecord = Record<string, { key: string }>

type FieldsOf<T> = T extends { kind: 'INPUT_OBJECT'; fields: infer F }
  ? F extends readonly any[]
    ? F[number]
    : never
  : T extends readonly any[]
    ? T[number]
    : T extends Record<string, infer V>
      ? V
      : never

type FieldKey<F> = F extends { key: infer K extends string } ? K : never

type SelectChoiceValue<F> = F extends {
  state: { selectChoices: readonly { value: infer V }[] }
}
  ? V
  : never

type FieldValue<F> = F extends { state: { inputKind: 'options_object' } }
  ? OptionsState
  : F extends { state: { inputKind: 'options_list' } }
    ? OptionsState[]
    : F extends { state: { inputKind: 'select' } }
      ? SelectChoiceValue<F>
      : F extends { state: { inputKind: 'boolean' } }
        ? boolean
        : F extends { state: { inputKind: 'number' } }
          ? number
          : F extends { state: { inputKind: 'text' | 'color' } }
            ? string
            : F extends { ofType: infer O }
              ? TsTypeOf<O>
              : unknown

type FieldPresentValue<F> = F extends { ofType: { isNullable: true } }
  ? FieldValue<F> | null
  : FieldValue<F>

type IsRequiredField<F> = F extends { isNullable: false } ? true : false

type HasNonNullDefault<F> = F extends { defaultValue: infer D }
  ? [D] extends [null | undefined]
    ? false
    : true
  : false

type RequiredFieldKeys<T> = {
  [F in FieldsOf<T> as IsRequiredField<F> extends true
    ? FieldKey<F>
    : never]: FieldPresentValue<F>
}

type OptionalFieldKeys<T> = {
  [F in FieldsOf<T> as IsRequiredField<F> extends true
    ? never
    : FieldKey<F>]?: FieldPresentValue<F>
}

export type OptionsTypeOf<T> = RequiredFieldKeys<T> & OptionalFieldKeys<T>

type ResolvedRequiredKeys<T> = {
  [F in FieldsOf<T> as IsRequiredField<F> extends true
    ? FieldKey<F>
    : HasNonNullDefault<F> extends true
      ? FieldKey<F>
      : never]: FieldPresentValue<F>
}

type ResolvedOptionalKeys<T> = {
  [F in FieldsOf<T> as IsRequiredField<F> extends true
    ? never
    : HasNonNullDefault<F> extends true
      ? never
      : FieldKey<F>]?: FieldPresentValue<F>
}

export type OptionsResolvedTypeOf<T> = ResolvedRequiredKeys<T> &
  ResolvedOptionalKeys<T>
